/**
 * Semántica de Excel que el modelo usa y hay que replicar exacto.
 * Cada función cita la fórmula del modelo donde se usa.
 */
import type { Num } from "../types";

export const isBlank = (v: unknown): v is null | undefined | "" => v === null || v === undefined || v === "";
export const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

/** VLOOKUP(x, tabla, 2, TRUE): última fila con key <= x (la tabla debe estar ordenada). null si x < primera key. */
export function vlookupApprox<T>(x: number, table: { key: number; value: T }[]): T | null {
  let found: T | null = null;
  for (const row of table) {
    if (row.key <= x) found = row.value;
    else break;
  }
  return found;
}

/** RANK(x, arr, 1) ascendente: 1 + cuántos valores son menores. (Sin empates en el modelo: la clave U incluye la fila.) */
export function rankAsc(x: number, arr: number[]): number {
  return 1 + arr.filter((v) => v < x).length;
}

/** MEDIAN(a, b, c) de tres números = el de en medio. El modelo lo usa como clamp en la cascada (O28:O37). */
export function median3(a: number, b: number, c: number): number {
  return [a, b, c].sort((p, q) => p - q)[1];
}

/** AVERAGE sobre un rango: ignora blancos y texto; si no queda nada → "" (null). */
export function averageSkipBlank(vals: Num[]): Num {
  const nums = vals.filter(isNum);
  return nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : null;
}

/** SUM: blancos y texto valen 0. */
export function sum(vals: Num[]): number {
  return vals.reduce<number>((acc, v) => acc + (isNum(v) ? v : 0), 0);
}

/** COUNT: cuenta solo números. */
export function count(vals: Num[]): number {
  return vals.filter(isNum).length;
}

/** ROUND(x, n) de Excel: redondea a la mitad lejos de cero (JS Math.round no). */
export function roundExcel(x: number, digits = 0): number {
  const m = Math.pow(10, digits);
  const v = Math.abs(x) * m;
  const r = Math.floor(v + 0.5 + 1e-9);
  return Math.sign(x) * (r / m);
}

/** ROUNDUP(x, 0): lejos de cero. */
export function roundUp(x: number): number {
  return x >= 0 ? Math.ceil(x - 1e-12) : -Math.ceil(-x - 1e-12);
}

/** ROUNDDOWN(x, 0): hacia cero. */
export function roundDown(x: number): number {
  return x >= 0 ? Math.floor(x + 1e-12) : -Math.floor(-x + 1e-12);
}

export function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

/** Normaliza texto para comparar menús: minúsculas, sin acentos, sin espacios dobles. */
export function norm(s: unknown): string {
  return String(s ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/** Igualdad como la hace Excel en MATCH/VLOOKUP exacto: sin distinguir mayúsculas. */
export function eqText(a: unknown, b: unknown): boolean {
  return String(a ?? "").trim().toLowerCase() === String(b ?? "").trim().toLowerCase();
}

/**
 * Comparación ">" de Excel entre un valor que puede ser texto y un número.
 * En Excel, cualquier texto (incluido "") es MAYOR que cualquier número. El modelo cae en esto
 * en 5 · Sistema F33 (`IFERROR(VLOOKUP(...),0)>0` con una brecha "" devuelve VERDADERO).
 */
export function excelGreaterThan(a: Num | string, b: number): boolean {
  if (typeof a === "string") return true;
  if (a === null) return 0 > b; // celda vacía → 0
  return a > b;
}
